import React, { useState } from "react";
import {
    Input, Badge, Table, FormGroup, Label,
    Card, CardBody
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import PropTypes from "prop-types";
import { NeoButton } from "../../../../components/Common";

/**
 * ObjectiveGrid
 * Gerencia a hierarquia Objetivos -> Tópicos.
 * Suporta Drag and Drop tanto para Objetivos quanto para Tópicos internos.
 */
const ObjectiveGrid = ({ objectives, onChange }) => {
    const [addingObjective, setAddingObjective] = useState(false);
    const [newObjectiveName, setNewObjectiveName] = useState("");

    // Handlers para Objetivos
    const handleAddObjective = () => {
        if (!newObjectiveName.trim()) return;

        const newObj = {
            id: crypto.randomUUID(),
            name: newObjectiveName,
            order: objectives.length,
            xp: 0,
            topics: []
        };

        onChange([...objectives, newObj]);
        setNewObjectiveName("");
        setAddingObjective(false);
    };

    const handleUpdateObjective = (id, field, value) => {
        const updated = objectives.map(obj =>
            obj.id === id ? { ...obj, [field]: value } : obj
        );
        onChange(updated);
    };

    const handleDeleteObjective = (id) => {
        onChange(objectives.filter(obj => obj.id !== id));
    };

    // Handlers para Tópicos
    const handleAddTopic = (objectiveId) => {
        const updated = objectives.map(obj => {
            if (obj.id === objectiveId) {
                const newTopic = {
                    id: crypto.randomUUID(),
                    name: "",
                    order: (obj.topics || []).length,
                    xp: 0,
                    isFundamental: false
                };
                return { ...obj, topics: [...(obj.topics || []), newTopic] };
            }
            return obj;
        });
        onChange(updated);
    };

    const handleUpdateTopic = (objectiveId, topicId, field, value) => {
        const updated = objectives.map(obj => {
            if (obj.id === objectiveId) {
                const updatedTopics = (obj.topics || []).map(t =>
                    t.id === topicId ? { ...t, [field]: value } : t
                );
                return { ...obj, topics: updatedTopics };
            }
            return obj;
        });
        onChange(updated);
    };

    const handleDeleteTopic = (objectiveId, topicId) => {
        const updated = objectives.map(obj => {
            if (obj.id === objectiveId) {
                return { ...obj, topics: (obj.topics || []).filter(t => t.id !== topicId) };
            }
            return obj;
        });
        onChange(updated);
    };

    // Ordenação DnD
    const handleOnDragEnd = (result) => {
        const { source, destination, type } = result;

        if (!destination) return;

        // Caso seja reordenação de OBJETIVOS
        if (type === "OBJECTIVE") {
            const items = Array.from(objectives);
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);

            const withNewOrders = items.map((item, index) => ({ ...item, order: index }));
            onChange(withNewOrders);
        }

        // Caso seja reordenação de TÓPICOS dentro de um objetivo
        if (type === "TOPIC") {
            const objectiveId = source.droppableId.split("-")[1];
            const updated = objectives.map(obj => {
                if (obj.id === objectiveId) {
                    const topics = Array.from(obj.topics || []);
                    const [reorderedTopic] = topics.splice(source.index, 1);
                    topics.splice(destination.index, 0, reorderedTopic);

                    const withNewOrders = topics.map((t, idx) => ({ ...t, order: idx }));
                    return { ...obj, topics: withNewOrders };
                }
                return obj;
            });
            onChange(updated);
        }
    };

    return (
        <div className="objective-grid mt-3">
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="objectives-list" type="OBJECTIVE">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {objectives.map((obj, index) => (
                                <Draggable key={obj.id} draggableId={obj.id} index={index}>
                                    {(providedObjective, snapshotObjective) => (
                                        <div
                                            ref={providedObjective.innerRef}
                                            {...providedObjective.draggableProps}
                                            className={`mb-4 ${snapshotObjective.isDragging ? "shadow-lg" : ""}`}
                                        >
                                            <Card className="border-0 bg-light-subtle shadow-sm rounded-3 overflow-hidden mb-0">
                                                <div className="d-flex align-items-center p-3 border-bottom bg-white">
                                                    <div {...providedObjective.dragHandleProps} className="me-3 cursor-move text-muted opacity-50">
                                                        <i className="mdi mdi-drag-vertical font-size-22"></i>
                                                    </div>
                                                    <Badge color="primary" pill className="me-3 font-size-12 px-3">{index + 1}</Badge>
                                                    <div className="flex-grow-1">
                                                        <Input
                                                            type="text"
                                                            value={obj.name}
                                                            onChange={(e) => handleUpdateObjective(obj.id, "name", e.target.value)}
                                                            className="border-0 fw-bold font-size-15 bg-transparent p-0"
                                                            placeholder="Nome do objetivo..."
                                                        />
                                                    </div>
                                                    <div className="flex-shrink-0 d-flex gap-2 ms-3">
                                                        <NeoButton
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            onClick={() => handleDeleteObjective(obj.id)}
                                                            icon="mdi-close-circle-outline"
                                                        />
                                                    </div>
                                                </div>

                                                <CardBody className="p-3">
                                                    <Droppable droppableId={`topics-${obj.id}`} type="TOPIC">
                                                        {(providedTopic) => (
                                                            <div className="table-responsive" {...providedTopic.droppableProps} ref={providedTopic.innerRef}>
                                                                <Table size="sm" className="table-borderless mb-0">
                                                                    <thead>
                                                                        <tr className="border-bottom font-size-11 text-muted text-uppercase fw-bold">
                                                                            <th style={{ width: '30px' }}></th>
                                                                            <th style={{ width: '40px' }}>#</th>
                                                                            <th>Avaliação / Tópico</th>
                                                                            <th style={{ width: '100px' }} className="text-center">Pontos</th>
                                                                            <th style={{ width: '120px' }} className="text-center">Fundamental?</th>
                                                                            <th style={{ width: '40px' }}></th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(obj.topics || []).map((topic, tIndex) => (
                                                                            <Draggable key={topic.id} draggableId={topic.id} index={tIndex}>
                                                                                {(providedT, snapshotT) => (
                                                                                    <tr
                                                                                        ref={providedT.innerRef}
                                                                                        {...providedT.draggableProps}
                                                                                        className={`align-middle border-bottom-dashed ${snapshotT.isDragging ? "bg-white shadow-sm" : ""}`}
                                                                                    >
                                                                                        <td>
                                                                                            <div {...providedT.dragHandleProps} className="cursor-move text-muted opacity-25">
                                                                                                <i className="mdi mdi-drag font-size-18"></i>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="text-muted small fw-bold">{index + 1}.{tIndex + 1}</td>
                                                                                        <td>
                                                                                            <Input
                                                                                                type="text"
                                                                                                value={topic.name}
                                                                                                onChange={(e) => handleUpdateTopic(obj.id, topic.id, "name", e.target.value)}
                                                                                                className="form-control-sm border-0 bg-transparent"
                                                                                                placeholder="Descreva o requisito..."
                                                                                            />
                                                                                        </td>
                                                                                        <td className="text-center">
                                                                                            <Input
                                                                                                type="number"
                                                                                                value={topic.xp}
                                                                                                onChange={(e) => handleUpdateTopic(obj.id, topic.id, "xp", Number(e.target.value))}
                                                                                                className="form-control-sm border-0 bg-light text-center rounded-2"
                                                                                                style={{ width: '70px', margin: '0 auto' }}
                                                                                            />
                                                                                        </td>
                                                                                        <td className="text-center">
                                                                                            <div className="form-check form-switch d-inline-block">
                                                                                                <Input
                                                                                                    type="switch"
                                                                                                    checked={topic.isFundamental}
                                                                                                    onChange={(e) => handleUpdateTopic(obj.id, topic.id, "isFundamental", e.target.checked)}
                                                                                                    className="form-check-input"
                                                                                                />
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="text-end">
                                                                                            <NeoButton
                                                                                                variant="link-danger"
                                                                                                size="sm"
                                                                                                className="text-muted p-0"
                                                                                                onClick={() => handleDeleteTopic(obj.id, topic.id)}
                                                                                                icon="mdi-trash-can-outline"
                                                                                            />
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </Draggable>
                                                                        ))}
                                                                        {providedTopic.placeholder}
                                                                    </tbody>
                                                                </Table>
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                    <NeoButton
                                                        variant="link"
                                                        size="sm"
                                                        className="p-0 mt-3 text-primary fw-bold font-size-12 hover-scale"
                                                        onClick={() => handleAddTopic(obj.id)}
                                                        icon="mdi-plus-circle-outline"
                                                    >
                                                        Novo Tópico / Avaliação
                                                    </NeoButton>
                                                </CardBody>
                                            </Card>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* Inclusão de Novo Objetivo */}
            <div className="mt-4 border-top pt-4">
                {addingObjective ? (
                    <div className="bg-white p-4 rounded-4 shadow border border-primary animate__animated animate__fadeInUp">
                        <FormGroup className="mb-3">
                            <Label className="fw-bold text-primary small mb-2 text-uppercase">Nome do Novo Objetivo</Label>
                            <Input
                                type="text"
                                value={newObjectiveName}
                                onChange={(e) => setNewObjectiveName(e.target.value)}
                                placeholder="Ex: Coordenação Motora, Força Muscular..."
                                className="form-control-lg rounded-3 border-primary-subtle"
                                autoFocus
                            />
                        </FormGroup>
                        <div className="d-flex justify-content-end gap-2">
                            <NeoButton variant="outline-secondary" size="sm" className="px-3" onClick={() => setAddingObjective(false)}>Cancelar</NeoButton>
                            <NeoButton variant="outline-primary" size="sm" className="px-4 shadow-sm" onClick={handleAddObjective}>Criar Objetivo</NeoButton>
                        </div>
                    </div>
                ) : (
                    <NeoButton
                        variant="outline-primary"
                        className="w-100 py-3 text-primary fw-bold bg-white rounded-3 border-2 shadow-sm transition-all hover-bg-primary-subtle"
                        onClick={() => setAddingObjective(true)}
                        icon="mdi-plus-circle-outline"
                    >
                        Adicionar Novo Objetivo à Malha
                    </NeoButton>
                )}
            </div>
        </div>
    );
};

ObjectiveGrid.propTypes = {
    objectives: PropTypes.array,
    onChange: PropTypes.func
};

export default ObjectiveGrid;
